<template>
  <div class="card-container">
    <div class="header-container">
      <div class="title">
        <div class="circle-symbol"></div>
        <div>{{ cardInfo.name }}</div>
        <el-icon v-if="props.cardInfo.groupKey == 'virtualMeters'" class="add-btn" @click="openAddVirtualMeterModal">
          <Plus />
        </el-icon>
        <el-icon v-if="props.cardInfo.groupKey == 'datumLines'" class="add-btn" @click="openAddDatumLineModal">
          <Plus />
        </el-icon>
      </div>
      <el-checkbox v-model="checkAll" :indeterminate="isIndeterminate" @change="handleCheckAllChange"></el-checkbox>
    </div>
    <div class="main-container">
      <el-scrollbar max-height="200px">
        <el-checkbox-group class="select-group" v-model="checkedItems" @change="handleCheckedItemsChange">
          <div class="select-item" v-for="item in itemList" :key="item">
            <!-- 选项的名称 -->
            <el-checkbox :label="item.label">{{ item.label }}</el-checkbox>
            <!-- 虚拟电表的操作btn区域 -->
            <div class="btn-container" v-if="props.cardInfo.groupKey == 'virtualMeters'">
              <el-button type="text" :icon="Edit" @click="openEditVirtualMeterModal(item)"></el-button>
              <el-button type="text" :icon="Delete" @click="onDeleteVirtualMeter(item)"></el-button>
            </div>
            <!-- 基准线的操作btn区域 -->
            <div class="btn-container" v-if="props.cardInfo.groupKey == 'datumLines'">
              <el-button type="text" :icon="Edit" @click="openEditDatumLineModal(item)"></el-button>
              <el-button type="text" :icon="Delete" @click="onDeleteDatumLine(item)"></el-button>
            </div>
            <!-- 【需量/月最大需量】的月份选择区域 -->
            <div class="btn-container" v-if="props.cardInfo.groupKey == 'allDemands' && item.key == '月最大需量'">
              <el-date-picker class="mmd-month-picker" v-model="mmdMonth" type="month" size="small"
                :placeholder="'请选择月份'" :clearable="false" @change="demandMonthChange"
                :disabled-date="disabledDate"></el-date-picker>
            </div>
          </div>
        </el-checkbox-group>
      </el-scrollbar>
    </div>
  </div>

  <!-- 弹窗-添加虚拟电表 -->
  <AddVirtualMeterModal v-if="isShowAddVmModal" v-model:show="isShowAddVmModal" :operateType="vmOperateType"
    :vMeterId="vMeterId" @updateVirtualMeter="updateVirtualMeter"
    @SendChange="isShowAddVmModal = false; vmOperateType = ''" />
  <!-- 弹窗-添加基准线 -->
  <AddDatumLineModal v-if="isShowAddDlModal" v-model:show="isShowAddDlModal" :operateType="dlOperateType" :dlId="dlId"
    :datumLines="itemList" @updateDatumLine="updateDatumLine"
    @SendChange="isShowAddDlModal = false; dlOperateType = ''" />
</template>

<script setup>
import { computed, onMounted, ref, defineProps, defineEmits, watch } from 'vue'
import { deleteDatumLineApi } from './api/index.js';
import { meterApi } from "@/api/modules/device/index.js";
import AddVirtualMeterModal from "./addVirtualMeterModal.vue"; // 弹窗-添加|编辑虚拟电表
import AddDatumLineModal from "./addDatumLineModal.vue"; // 弹窗-添加|编辑基准线
import { Edit, Delete, Plus } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import dayjs from 'dayjs';

const $str = window.$str;

const props = defineProps({
  cardInfo: {
    type: Object,
    default: null,
    required: true,
  },
  config: {
    type: Object,
    default: null,
    required: true,
  },
})
const itemList = ref(props.cardInfo.data);
const emit = defineEmits(['selectLinkChanged', 'updateVirtualMeter', 'updateDatumLine', 'updateMmdLine']);
const mmdMonth = ref(dayjs().subtract(1, 'month').format('YYYY-MM-DD')) // monthlyMaxDemandMonth -- 【月最大需量】对应的月份
const isShowAddVmModal = ref(false); // 是否展示【新增虚拟电表】弹窗
const vmOperateType = ref('');
const vMeterId = ref('');
const isShowAddDlModal = ref(false); // 是否展示【新增基准线】弹窗
const dlOperateType = ref('');
const dlId = ref('');

const checkAll = ref(false);
const checkedItems = ref([]); // 选项组-被选中的项
const isIndeterminate = computed(() => { // 全选框-是否处于半选状态
  const checkedListLength = checkedItems.value.length;
  if (checkedListLength > 0 && checkedListLength < itemList.value.length) {
    return true;
  } else {
    return false;
  }
});

const handleCheckAllChange = (val) => {
  checkedItems.value = val ? itemList.value.map(it => it.label) : []
  isIndeterminate.value = false
  sendMsg();
}

const handleCheckedItemsChange = (value) => {
  checkAll.value = value.length === itemList.value.length;
  sendMsg();
}

const updateCheckedStates = () => {
  const defaultChecked = [];
  itemList.value.map(it => {
    if (it.isShow) {
      defaultChecked.push(it.label);
    }
  })
  checkedItems.value = defaultChecked;
  checkAll.value = itemList.value.length != 0 && checkedItems.value.length == itemList.value.length;
}

onMounted(() => {
  updateCheckedStates();
})

watch(() => props.config.refreshTime, (nv, ov) => {
  itemList.value = props.config.getNewData(props.cardInfo.groupKey);
  updateCheckedStates();
})

function sendMsg() {
  emit('selectLinkChanged', {
    groupKey: props.cardInfo.groupKey,
    checkedLinks: checkedItems.value.map(label => itemList.value.find(it => it.label == label)),
  });
}


/** ======[虚拟电表相关的处理方法]=========================================================================================  */

/** 【虚拟电表信息】发生变化的响应方法 */
function updateVirtualMeter(msg) {
  emit('updateVirtualMeter', msg);
}

/** 打开【新增虚拟电表】的弹窗 */
function openAddVirtualMeterModal() {
  vmOperateType.value = 'add';
  vMeterId.value = '';
  isShowAddVmModal.value = true;
}

/** 打开【编辑虚拟电表】的弹窗 */
function openEditVirtualMeterModal(meter) {
  vmOperateType.value = 'edit';
  vMeterId.value = meter.key;
  isShowAddVmModal.value = true;
}

/** 【删除虚拟电表】的响应方法 */
async function onDeleteVirtualMeter(meter) {
  const confirmMessage = `${$str.getKey('数据分析/是否删除虚拟电表')}【${meter.label}】？`;
  const userAgree = await ElMessageBox.confirm(confirmMessage, $str.getKey('数据分析/系统提示'), {
    confirmButtonText: $str.getKey('数据分析/确定'),
    cancelButtonText: $str.getKey('数据分析/取消'),
    "show-close": false,
    confirmButtonClass: 'custom-confirm-btn',
    cancelButtonClass: 'custom-cancel-btn',
    type: "warning"
  }).then(res => {
    return true;
  }).catch(err => {
    return false
  });

  if (!userAgree) { return false; }

  meterApi.deleteVMeter(meter.key).then(res => {
    if (res.code == 200) {
      window.dispatchNotify({
        type: 'success',
        message: $str.getKey('数据分析/删除成功')
      });
      updateVirtualMeter('delete');
    }
  }).catch(err => {
    console.error('虚拟电表删除失败：', err)
    window.dispatchNotify({
      type: 'error',
      message: $str.getKey('数据分析/删除失败')
    })
  })
}


/** ======[基准线相关的处理方法]=========================================================================================  */

/** 【基准线信息】发生变化的响应方法 */
function updateDatumLine(msg) {
  emit('updateDatumLine', msg);
}

/** 打开【新增基准线】的弹窗 */
function openAddDatumLineModal() {
  // 判断是否达到上限
  if (itemList.value.length >= 5) {
    window.dispatchNotify({
      type: 'info',
      message: $str.getKey('数据分析/基准线总数量已达到上限')
    })
    return false;
  }
  dlOperateType.value = 'add';
  dlId.value = '';
  isShowAddDlModal.value = true;
}

/** 打开【编辑基准线】的弹窗 */
function openEditDatumLineModal(datumLine) {
  dlOperateType.value = 'edit';
  dlId.value = datumLine.key;
  isShowAddDlModal.value = true;
}

/** 【删除基准线】的响应方法 */
async function onDeleteDatumLine(datumLine) {
  const confirmMessage = `${$str.getKey('数据分析/是否删除基准线')}【${datumLine.label}】？`;
  const userAgree = await ElMessageBox.confirm(confirmMessage, $str.getKey('数据分析/系统提示'), {
    confirmButtonText: $str.getKey('数据分析/确定'),
    cancelButtonText: $str.getKey('数据分析/取消'),
    "show-close": false,
    confirmButtonClass: 'custom-confirm-btn',
    cancelButtonClass: 'custom-cancel-btn',
    type: "warning"
  }).then(res => {
    return true;
  }).catch(err => {
    return false
  });

  if (!userAgree) { return false; }

  deleteDatumLineApi(datumLine.key).then(res => {
    if (res.code == 200) {
      window.dispatchNotify({
        type: 'success',
        message: $str.getKey('数据分析/删除成功')
      });
      updateDatumLine('delete');
    }
  }).catch(err => {
    console.error('基准线删除失败：', err)
    window.dispatchNotify({
      type: 'error',
      message: $str.getKey('数据分析/删除失败')
    })
  })
}



/** ======[月最大需量相关的处理方法]=========================================================================================  */
function demandMonthChange(month) {
  emit('updateMmdLine', month);
}


/** 设置日期的禁选范围 */
function disabledDate(date) {
  const latestHalfYear = dayjs().subtract(6, 'month');
  return dayjs(date) > dayjs() || dayjs(date) < latestHalfYear;
}

</script>

<style lang="scss" scoped>
.card-container {
  min-height: 100px;
  max-height: 300px;
  box-sizing: border-box;
  margin: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(192, 207, 218, 0.3);
  border-radius: 12px;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-family: "HarmonyOS Sans SC", sans-serif;
      color: rgba(46, 46, 46, 0.85);
      font-weight: 400;

      .circle-symbol {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #3a68ff;
      }

      .add-btn {
        color: rgba(46, 46, 46, 0.5);
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #3a68ff;
        }
      }
    }

    ::v-deep .el-checkbox {
      .el-checkbox__input {
        .el-checkbox__inner {
          background-color: #FFFFFF;
          border-color: rgba(192, 207, 218, 0.3);
          width: 16px;
          height: 16px;

          &:after {
            border-color: #3a68ff;
          }
        }

        &.is-checked {
          .el-checkbox__inner {
            background-color: #3a68ff;
            border-color: #3a68ff;

            &:after {
              border-color: #FFFFFF;
            }
          }
        }

        &.is-indeterminate {
          .el-checkbox__inner {
            background-color: #3a68ff;
            border-color: #3a68ff;

            &:before {
              background-color: #FFFFFF;
            }
          }
        }
      }

      .el-checkbox__label {
        color: rgba(46, 46, 46, 0.85);
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
      }
    }
  }

  .main-container {
    margin: 10px 0;
    width: 100%;
    max-height: 230px;

    .el-scrollbar {
      width: 100%;
      flex: 1;
      padding-right: 20px;
    }

    .select-group {
      display: flex;
      flex-direction: column;

      /** 设置为UI上的样式 */
      .select-item {
        position: relative;
        display: flex;
        justify-content: space-between;

        ::v-deep .el-checkbox {
          width: 100%;
          padding: 0 10px;
          border-radius: 6px;
          margin: 2px 0;
          transition: all 0.2s;

          &.is-checked {
            background: rgba(58, 104, 255, 0.1);
          }

          .el-checkbox__input.is-checked {
            .el-checkbox__inner {
              background-color: #3a68ff;
              border-color: #3a68ff;

              &::after {
                border-color: #FFFFFF;
              }
            }

            &+.el-checkbox__label {
              color: #3a68ff !important;
              font-family: "HarmonyOS Sans SC", sans-serif;
              font-size: 14px;
              font-weight: 500;
            }
          }

          .el-checkbox__input:not(.is-checked) {
            .el-checkbox__inner {
              background-color: #FFFFFF;
              border-color: rgba(192, 207, 218, 0.3);
            }

            &+.el-checkbox__label {
              color: rgba(46, 46, 46, 0.85) !important;
              font-family: "HarmonyOS Sans SC", sans-serif;
              font-size: 14px;
            }
          }
        }

        .btn-container {
          position: absolute;
          height: 100%;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 10px;

          ::v-deep .el-icon {
            font-size: 16px;
            transition: color 0.2s;
            color: rgba(46, 46, 46, 0.65);

            &:hover {
              color: #3a68ff;
            }
          }

          ::v-deep .mmd-month-picker {
            width: 90px;

            .el-input__wrapper {
              background: transparent
            }
          }
        }

        // 选中状态下，图标颜色变为蓝色
        &:has(.el-checkbox.is-checked) .btn-container {
          ::v-deep .el-icon {
            color: #3a68ff;

            &:hover {
              color: #5B8DF4;
            }
          }
        }
      }
    }
  }
}
</style>